// src/features/admin/orders/MarkerRowEditor.tsx
import React from "react";
import Button from "components/ui/Button";

export interface MarkerFormState {
  code: string;
  name: string;
  value: string;
  unit: string;
  ref_low: string;   // string in form; converted on submit
  ref_high: string;  // string in form; converted on submit
  flag: string;      // derived; not user editable
  notes: string;     // per-marker notes
}

export interface MarkerFieldErrors {
  value?: string;
}

interface Props {
  marker: MarkerFormState;
  errors?: MarkerFieldErrors;
  onChange: (patch: Partial<MarkerFormState>) => void;
  onRemove: () => void; // clears editable fields
}

const inputClass =
  "w-full px-2 py-1 border rounded focus:outline-none focus:ring focus:border-blue-300";

function toNum(s: string): number | null {
  const raw = String(s ?? "").trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function deriveFlag(value: string, refLow: string, refHigh: string): string {
  const v = toNum(value);
  if (v == null) return "";

  const lo = toNum(refLow);
  const hi = toNum(refHigh);

  if (lo != null && v < lo) return "L";
  if (hi != null && v > hi) return "H";

  if (lo != null || hi != null) return "N";
  return "";
}

const MarkerRowEditor: React.FC<Props> = ({ marker, errors, onChange, onRemove }) => {
  const filled = !!marker.value.trim();
  const rowBg = filled ? "bg-slate-200" : "bg-white";

  const displayName = marker.name?.trim() || marker.code?.trim() || "Marker";

  return (
    <div className={`border rounded-lg ${rowBg} p-2`}>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
        {/* Left: fixed marker label */}
        <div className="sm:col-span-3">
          <div className="text-sm font-semibold text-slate-900">{displayName}</div>
          {marker.code?.trim() && (
            <div className="text-xs text-slate-500 mt-0.5">{marker.code}</div>
          )}
        </div>

        {/* Unit (editable) */}
        <div className="sm:col-span-1">
          <input
            className={inputClass}
            value={marker.unit}
            onChange={(e) => onChange({ unit: e.target.value })}
            placeholder="Unit"
            aria-label={`${displayName} unit`}
          />
        </div>

        {/* Value */}
        <div className="sm:col-span-2">
          <input
            className={inputClass}
            value={marker.value}
            onChange={(e) => {
              const nextValue = e.target.value;
              onChange({
                value: nextValue,
                flag: deriveFlag(nextValue, marker.ref_low, marker.ref_high),
              });
            }}
            placeholder="Value"
          />
          {errors?.value && <div className="text-xs text-red-600 mt-1">{errors.value}</div>}
        </div>

        {/* Ref low */}
        <div className="sm:col-span-2">
          <input
            className={inputClass}
            value={marker.ref_low}
            onChange={(e) => {
              const nextLow = e.target.value;
              onChange({
                ref_low: nextLow,
                flag: deriveFlag(marker.value, nextLow, marker.ref_high),
              });
            }}
            placeholder="Ref low"
          />
        </div>

        {/* Ref high */}
        <div className="sm:col-span-2">
          <input
            className={inputClass}
            value={marker.ref_high}
            onChange={(e) => {
              const nextHigh = e.target.value;
              onChange({
                ref_high: nextHigh,
                flag: deriveFlag(marker.value, marker.ref_low, nextHigh),
              });
            }}
            placeholder="Ref high"
          />
        </div>

        {/* Notes */}
        <div className="sm:col-span-2">
          <input
            className={inputClass}
            value={marker.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="Notes (optional)"
          />
        </div>

        {/* Clear */}
        <div className="sm:col-span-12 flex justify-end">
          <Button type="button" variant="secondary" onClick={onRemove}>
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MarkerRowEditor;